import { thousandSeparator } from "../../utils/constants";
import { CURRENCY_SYMBOLS, CURRENCY_TYPES, CYCLE_TYPES, PLAN_CARD_FEATURES, PLANS } from "./constant.billing";
import { getAddOnsResponse, PricingPlan, PricingPlanFeatureLimit, recurringAddOnsModel, billingSubscriptionAddOn, billingSubscriptionPlan, subscribePlanAddOn, billingSubscriptionFeatureUsage, billingSubscriptionDetailsResponse } from "../../models/axiosModel";

type BillingAddOnLike = recurringAddOnsModel | billingSubscriptionAddOn | getAddOnsResponse;

type BillingSubscriptionPlanLike = Pick<
    billingSubscriptionPlan,
    "recurring_add_ons" | "one_time_add_ons"
>;

export const getBillingPriceSuffix = (cycle: string) => {
    const cycleName = cycle?.toLowerCase();

    switch (cycleName) {
        case CYCLE_TYPES.DAILY:
            return '/day';
        case CYCLE_TYPES.WEEKLY:
            return '/week';
        case CYCLE_TYPES.MONTHLY:
            return '/month';
        case CYCLE_TYPES.YEARLY:
        case CYCLE_TYPES.ANNUALLY:
            return '/year';
        default:
            return `${cycleName}`;
    }
};

export const getCycleLabel = (cycle: string) => {
    const cycleName = cycle?.toLowerCase() || CYCLE_TYPES.MONTHLY;

    switch (cycleName) {
        case CYCLE_TYPES.DAILY:
            return 'day';
        case CYCLE_TYPES.WEEKLY:
            return 'week';
        case CYCLE_TYPES.MONTHLY:
            return 'month';
        case CYCLE_TYPES.YEARLY:
        case CYCLE_TYPES.ANNUALLY:
            return 'year';
        default:
            return cycleName;
    }
};

export const getBillingCycleName = (cycle: string) => {
    const cycleName = cycle?.toLowerCase() || CYCLE_TYPES.MONTHLY;

    switch (cycleName) {
        case CYCLE_TYPES.DAILY:
            return 'daily';
        case CYCLE_TYPES.WEEKLY:
            return 'weekly';
        case CYCLE_TYPES.MONTHLY:
            return 'monthly';
        case CYCLE_TYPES.YEARLY:
        case CYCLE_TYPES.ANNUALLY:
            return 'yearly';
        default:
            return cycleName;
    }
};

export const getCurrencySymbol = (currency: string) => {
    const code = currency?.toUpperCase();
    if (code === CURRENCY_TYPES.USD || currency === '$') return CURRENCY_SYMBOLS.USD;
    return CURRENCY_SYMBOLS.INR;
};

export const isAnnualCycle  = (cycle?: string | null) => {
    const name = cycle?.toLowerCase();
    return name === CYCLE_TYPES.ANNUALLY || name === CYCLE_TYPES.YEARLY;
};

export const getPlanDisplayAmount = (amount?: number | string | null, cycle?: string | null) => {
    const value = Number(amount) || 0;
    return isAnnualCycle(cycle) ? Math.round(value / 12) : value;
};

export const getPlanDisplayPriceSuffix = (cycle?: string | null) => {
    return isAnnualCycle(cycle)
        ? getBillingPriceSuffix(CYCLE_TYPES.MONTHLY)
        : getBillingPriceSuffix(cycle || CYCLE_TYPES.MONTHLY);
};

export const filterPricingPlansByTabs = (
    plans: PricingPlan[] | null | undefined,
    cycle: string,
    currency: string
): PricingPlan[] => {

    if (!plans?.length) return [];

    const cycleName = cycle?.toLowerCase();
    const currencyCode = currency?.toUpperCase();

    return plans.filter((plan) => {
        // Always show Free Plan - regardless of tab
        if (plan.billing_plan_name?.toLowerCase() === PLANS.FREE) return true;

        const planCycle = plan.billing_plan_cycle_period_name?.toLowerCase();
        const cycleMatch = isAnnualCycle(cycleName)
            ? isAnnualCycle(planCycle)
            : planCycle === CYCLE_TYPES.MONTHLY;

        return cycleMatch && plan.billing_plan_currency?.toUpperCase() === currencyCode;
    });
};

export const filterAddOnsByTabs = (
    addOns: getAddOnsResponse[] | null | undefined,
    _cycle: string,
    currency: string
): getAddOnsResponse[] => {

    if (!addOns?.length) return [];

    const currencyCode = currency?.toUpperCase();

    return addOns.filter(
        (addOn) => addOn.billing_add_on_currency?.toUpperCase() === currencyCode
    );
};

export const isFreePlan = (plans: PricingPlan[], planId: BillingId) => {
    const plan = plans?.find((item) => String(item.billing_plan_id) === String(planId));
    return plan?.billing_plan_name?.toLowerCase() === PLANS.FREE;
};

export const isCurrentPlanPublic = (plan: billingSubscriptionPlan) => {
    return plan?.billing_plan_public === true;
};

/** Save % vs paying monthly for a year. Annual plan amount is the full-year price. */
export const getAnnualSavePercent = (plans: PricingPlan[] | null | undefined, currency: string) => {
    if (!plans?.length) return null;

    const currencyCode = currency?.toUpperCase();
    const paidPlans = plans.filter(
        (plan) => plan.billing_plan_name?.toLowerCase() !== PLANS.FREE
            && plan.billing_plan_currency?.toUpperCase() === currencyCode
    );

    const monthlyPlan = paidPlans.find(
        (plan) => plan.billing_plan_cycle_period_name?.toLowerCase() === CYCLE_TYPES.MONTHLY
    );
    const annualPlan = paidPlans.find(
        (plan) => plan.billing_plan_name?.toLowerCase() === monthlyPlan?.billing_plan_name?.toLowerCase()
            && isAnnualCycle(plan.billing_plan_cycle_period_name)
    );

    const monthlyPrice = Number(monthlyPlan?.billing_plan_amount) || 0;
    const annualPrice = Number(annualPlan?.billing_plan_amount) || 0;
    const yearlyIfBilledMonthly = monthlyPrice * 12;

    if (!yearlyIfBilledMonthly || !annualPrice || annualPrice >= yearlyIfBilledMonthly) return null;

    return Math.round(((yearlyIfBilledMonthly - annualPrice) / yearlyIfBilledMonthly) * 100);
};

// Query params: /plans?plan=<id>&addon=<id>&qty=<number>&rec=<boolean> (repeat addon/qty/rec for multiple)
export const QUERY_PARAM_KEYS = {
    plan: "plan",
    addon: "addon",
    qty: "qty",
    rec: "rec",
} as const;

export type BillingId = string | number;

const parseId = (value: string | null) => {
    const trimmed = value?.trim();
    return trimmed || null;
};

const parsePositiveInt = (value: string | null) => {
    if (!value) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const parseBool = (value: string | null | undefined) => {
    if (!value) return false;
    return ["1", "true", "yes"].includes(value.toLowerCase());
};

export const parseQueryParams = (searchParams: URLSearchParams) => {
    const plan = parseId(searchParams.get(QUERY_PARAM_KEYS.plan));
    const addons = searchParams.getAll(QUERY_PARAM_KEYS.addon);
    const qtys = searchParams.getAll(QUERY_PARAM_KEYS.qty);
    const recs = searchParams.getAll(QUERY_PARAM_KEYS.rec);

    const addOns = addons.reduce<subscribePlanAddOn[]>((acc, raw, index) => {
        const billing_add_on_id = parseId(raw);
        if (!billing_add_on_id) return acc;

        acc.push({
            billing_add_on_id,
            quantity: parsePositiveInt(qtys[index]) || 1,
            flag_recurring: parseBool(recs[index]),
        });

        return acc;
    }, []);

    return { plan, addOns };
};

// Plans Features
export const getPricingPlanFeatures = (plan: PricingPlan): string[] => {
    const features = plan?.features;
    
    if (!features) return [];

    const allFeatures = (Object.values(features) as PricingPlanFeatureLimit[][]).flat();
    const cycleSuffix = getCycleLabel(plan.billing_plan_cycle_period_name);
    const cycleLabel = cycleSuffix.charAt(0).toUpperCase() + cycleSuffix.slice(1);

    return PLAN_CARD_FEATURES
        .map(({ featureKey, showCycleSuffix }) => {
            const feature = allFeatures.find(
                (item) => item?.billing_feature_name?.toLowerCase() === featureKey
            );
            if (!feature) return null;

            const limit = feature.billing_plan_feature_limit;
            if (limit === undefined || limit === null || limit === "") return null;

            const label = feature.billing_feature_display_name || feature.billing_feature_name;
            const formattedLimit = isNaN(Number(limit)) ? limit : thousandSeparator(Number(limit));

            return showCycleSuffix
                ? `${formattedLimit} ${label} / ${cycleLabel}`
                : `${formattedLimit} ${label}`;
        })
        .filter(Boolean) as string[];
};

// Add-Ons Utils
export const getSubscriptionPlanAddOns = (plan?: BillingSubscriptionPlanLike | null): Array<billingSubscriptionAddOn> => {
    return [
        ...(plan?.one_time_add_ons || []),
        ...(plan?.recurring_add_ons || []),
    ];
};

export const isSubscriptionOneTimeAddOn = (
    plan: BillingSubscriptionPlanLike | null | undefined,
    addOn: billingSubscriptionAddOn
) => {
    const addOnId = String(addOn.billing_add_on_id);
    const oneTimeAddOnIds = new Set(
        (plan?.one_time_add_ons || []).map((item) => String(item.billing_add_on_id))
    );
    const recurringAddOnIds = new Set(
        (plan?.recurring_add_ons || []).map((item) => String(item.billing_add_on_id))
    );

    return oneTimeAddOnIds.has(addOnId) && !recurringAddOnIds.has(addOnId);
};

export const isAiCreditsAddOn = (addOn: BillingAddOnLike) => {
    return getAddOnName(addOn).toLowerCase().includes("ai credit");
};

export const isQuantityBasedAddOn = (addOn: getAddOnsResponse) => {
    return addOn.billing_feature_id !== null && addOn.billing_feature_id !== undefined;
};

export const isRemoveBrandingAddOn = (addOn: getAddOnsResponse) => {
    return addOn.billing_add_on_name?.toLowerCase().includes("branding");
};

// Current Plan Card Utils
export const getAddOnName = (addOn: BillingAddOnLike) => {
    return addOn.billing_add_on_name || "";
}

export const getAddOnTitle = (addOn: BillingAddOnLike) => {
    const name = addOn.billing_add_on_name;
    if (!isAiCreditsAddOn(addOn)) return name;

    const qty = Math.max(('quantity' in addOn ? addOn.quantity : undefined) || 1, 1);
    return `${name} • ${qty} ${qty === 1 ? "pack" : "packs"}`;
};

export const getAddOnDescription = (addOn: BillingAddOnLike) => {
    if (isAiCreditsAddOn(addOn)) {
        const perPack = Number((addOn as recurringAddOnsModel).billing_plan_feature_limit) || 0;
        const quantity = Math.max(('quantity' in addOn ? addOn.quantity : undefined) || 1, 1);

        if (perPack > 0) {
            return `${thousandSeparator(perPack * quantity)} credits`;
        }

        return addOn.billing_add_on_description || "";
    }

    return addOn.billing_add_on_description || "";
};

export const getAddOnAmount = (addOn: BillingAddOnLike) => {
    const amount = Number(addOn.billing_add_on_amount) || 0;
    const quantity = Math.max(('quantity' in addOn ? addOn.quantity : undefined) || 1, 1);

    return amount * quantity;
};

// Plans Add-Ons Utils
export const getChangePlanAddOnTotalAmount = (addOn: getAddOnsResponse, quantity: number, cycle?: string | null) => {
    const unitAmount = addOn.billing_add_on_amount || 0;
    const qty = Math.max(quantity, 0);

    if (qty <= 0) return 0;

    if (addOn?.flag_charge_annually_upfront && isAnnualCycle(cycle)) {
        return unitAmount * qty * 12;
    }

    return unitAmount * qty;
};

export const getChangePlanAddOnDescriptionText = (addOn: getAddOnsResponse, quantity: number, isRecurring = false) => {
    
    if (!isQuantityBasedAddOn(addOn)) {
        return addOn.billing_add_on_description || '';
    }

    const qty = Math.max(quantity, 1);
    const packLimit = Number(addOn.billing_plan_feature_limit) || 0;
    const totalCredits = packLimit * qty;

    const packLabel = qty === 1 ? 'pack' : 'packs';
    const creditLabel = `${thousandSeparator(totalCredits)} credits`;
    
    const currencySymbol = addOn.billing_add_on_currency_symbol || addOn.billing_add_on_currency;
    const addOnAmount = thousandSeparator(addOn.billing_add_on_amount || 0);
    const priceLabel = `${currencySymbol} ${addOnAmount}/pack`;

    if (qty === 0) {
        return `${thousandSeparator(packLimit)} credits per pack • ${priceLabel}`;
    }

    if (isRecurring || qty > 0) {
        return `${qty} ${packLabel} = ${creditLabel} • ${priceLabel} • carry forward`;
    }

    return `${creditLabel} • ${priceLabel} • carry forward`;
};

// Billing Usage Utils
export const usageLimits = (billingSubscriptionDetails: billingSubscriptionDetailsResponse | null) => {
    return billingSubscriptionDetails?.feature_usage
        ?.find((featureType: billingSubscriptionFeatureUsage) => 
            featureType.billing_feature_type_name.toLowerCase() === 'usage limits');
}
    
export const channelDeploymentLimits = (billingSubscriptionDetails: billingSubscriptionDetailsResponse | null) => {
    return billingSubscriptionDetails?.feature_usage
        ?.find((featureType: billingSubscriptionFeatureUsage) => 
            featureType.billing_feature_type_name.toLowerCase() === 'channel deployment limits');
}

export const shouldShowAddOns = (plan: billingSubscriptionPlan) => {
    return plan && isCurrentPlanPublic(plan);
}
